import { useEffect, useState } from "react";
import { fetchLeaderboardData } from "@/api/vercel";

// Define types inline to avoid import issues
interface Timestamp {
  _seconds: number;
  _nanoseconds: number;
}

interface HorseData {
  number: number;
  name: string;
  jockey?: string;
  trainer?: string;
  silks?: string;
  currentPrice?: number;
  rank?: number;
  selectionId?: number;
  [key: string]: any;
}

interface BetfairResults {
  winner?: {
    selectionId: number;
    name?: string;
  };
  placeGetters?: Array<{
    selectionId: number;
    name?: string;
  }>;
  scratchings?: Array<{
    selectionId: number;
    name?: string;
  }>;
  [key: string]: any;
}

interface RacePrediction {
  race: string | number;
  track: string;
  date: string;
  firstFourData?: HorseData[];
  marketStartTime?: string;
  analysis?: string;
  betfairResults?: BetfairResults;
  podium?: string[];
  winReturns?: number;
  placeReturns?: number;
  totalReturns?: number;
  archivedAt?: Timestamp;
  [key: string]: any;
}

interface ArchiveResult {
  id?: string;
  predictionData: RacePrediction;
  betfairResults?: BetfairResults;
  podium?: string[];
  winReturns?: number;
  placeReturns?: number;
  totalReturns?: number;
  archivedAt?: Timestamp;
  [key: string]: any;
}

interface ApiResponse {
  status: string;
  data: Record<string, any>;
  message?: string;
}

// The URL of the new API endpoint
// Use the correct Vite environment variable syntax
// @ts-ignore - Ignore the TypeScript error for import.meta
const workspace = import.meta.env.VITE_APP_WORKSPACE || "dev";
let API_URL: string;
if (workspace === "dev" || workspace === "staging" || workspace === "prod") {
  API_URL = `https://lexi-${workspace}-front-end-mcdr62tc5a-ts.a.run.app`;
} else {
  API_URL = `https://australia-southeast1-staible-returns.cloudfunctions.net/staible-returns-${workspace}-front-end`;
}

export const useTodaysResults = () => {
  const [predictions, setPredictions] = useState<RacePrediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Use fetchLeaderboardData from the Vercel API client
        const apiResponse: ApiResponse = await fetchLeaderboardData({
          collection: "results-archive",
        });

        if (apiResponse.status === "success" && apiResponse.data) {
          // Convert the object of archive results to an array
          const archiveResults = Object.values(
            apiResponse.data,
          ) as ArchiveResult[];

          // Extract prediction data from each archive result
          const extractedPredictions = archiveResults
            .filter(
              (result) =>
                result &&
                typeof result === "object" &&
                result.predictionData &&
                typeof result.predictionData === "object" &&
                "race" in result.predictionData &&
                "track" in result.predictionData,
            )
            .map((result) => ({
              ...result.predictionData,
              // Add result data that might be useful
              betfairResults: result.betfairResults,
              podium: result.podium,
              winReturns: result.winReturns,
              placeReturns: result.placeReturns,
              totalReturns: result.totalReturns,
              archivedAt: result.archivedAt,
            }));

          // Sort by date (most recent first)
          extractedPredictions.sort((a, b) => {
            // First try to sort by archivedAt timestamp if available
            if (a.archivedAt?._seconds && b.archivedAt?._seconds) {
              return b.archivedAt._seconds - a.archivedAt._seconds;
            }

            // Fall back to date string comparison (reverse order)
            const dateA = a.date.split("-").reverse().join("-");
            const dateB = b.date.split("-").reverse().join("-");
            return dateB.localeCompare(dateA);
          });

          setPredictions(extractedPredictions);
        } else {
          setPredictions([]);
          setError(new Error("Invalid API response format"));
        }
      } catch (err) {
        console.error("Error fetching predictions:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { predictions, loading, error };
};
